import { useHover, usePress } from "@react-aria/interactions";
import { useRadio } from "@react-aria/radio";
import { mergeProps } from "@react-aria/utils";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  ForwardedRef,
  PropsWithChildren,
  Ref,
  forwardRef,
  useContext,
  useRef,
} from "react";
import { DefaultTheme, StyledComponentProps } from "styled-components";
import { PropsWithPropStyling } from "../../shared";
import { TextStyleProps } from "../../shared/textStyles";
import { AsProp } from "../../shared/types";
import { ItemContainer } from "./Elements/ItemContainer";
import {
  ActiveItemContentPlaceholder,
  ItemContent,
} from "./Elements/ItemContent";
import { ToggleGroupContext } from "./ToggleGroupContext";

export type ToggleGroupItemBaseProps<
  As extends AsProp,
  ContentAs extends AsProp
> = PropsWithChildren<{
  as?: As;
  contentAs?: ContentAs;
  contentProps?: StyledComponentProps<
    ContentAs,
    DefaultTheme,
    PropsWithPropStyling<TextStyleProps>,
    never
  >;
  inputRef?: ForwardedRef<HTMLInputElement>;
  value: string;
}>;

export type ToggleGroupItemProps<
  As extends AsProp = any,
  ContentAs extends AsProp = any
> = PropsWithPropStyling<
  ToggleGroupItemBaseProps<As, ContentAs> &
    TextStyleProps &
    ComponentPropsWithoutRef<As>
>;

export const ToggleGroupItem = forwardRef(renderToggleGroupItem) as <
  As extends AsProp = "label",
  ContentAs extends AsProp = "span"
>(
  props: ToggleGroupItemProps<As, ContentAs> & {
    ref?: ForwardedRef<ElementRef<As>>;
  }
) => ReturnType<typeof renderToggleGroupItem>;

function renderToggleGroupItem<As extends AsProp, ContentAs extends AsProp>(
  {
    as,
    children,
    contentAs,
    contentProps,
    inputRef: inputRefProp,
    value,
    ...containerProps
  }: ToggleGroupItemProps<As, ContentAs>,
  ref: ForwardedRef<ElementRef<As>>
) {
  const contextState = useContext(ToggleGroupContext);

  if (!contextState)
    throw new Error("ToggleGroup.Item must be a child of ToggleGroup.Root");

  const { state, size } = contextState;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputRef: Ref<HTMLInputElement> = (ref) => {
    inputRef.current = ref;
    if (!inputRefProp) return;
    if (inputRefProp instanceof Function) inputRefProp(ref);
    else inputRefProp.current = ref;
  };

  const { hoverProps, isHovered } = useHover({});
  const { isPressed: isLabelPressed, pressProps } = usePress({});

  const { inputProps, ...radioState } = useRadio(
    { children, value },
    state,
    inputRef
  );

  return (
    <ItemContainer
      size={size}
      as={as as undefined}
      {...containerProps}
      ref={ref}
    >
      <VisuallyHidden>
        <input {...inputProps} ref={handleInputRef} />
      </VisuallyHidden>{" "}
      <ItemContent
        as={contentAs as undefined}
        {...mergeProps(hoverProps, pressProps)}
        {...radioState}
        {...contentProps}
        isHovered={isHovered}
        isInvalid={state.validationState === "invalid"}
        isPressed={isLabelPressed || radioState.isPressed}
      >
        {children}
      </ItemContent>
      {/* Having this additional content clone prevents the width from changing
          when switching checked state. The jumping behavior is a result of a wider
          font being used for the active item. */}
      <ActiveItemContentPlaceholder
        aria-hidden
        as={contentAs as undefined}
        {...contentProps}
      >
        {children}
      </ActiveItemContentPlaceholder>
    </ItemContainer>
  );
}
